// src/components/TeamHistory.js
import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import useTeamHistory from '../hooks/useTeamHistory';

export default function TeamHistory({ teamScores }) {
    const { t } = useLanguage();
    const { historyData, loading, error } = useTeamHistory(teamScores);

    if (loading) {
        return (
            <div className="bg-gray-800 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold mb-4">{t('teamHistory') || 'Team History'}</h3>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-gray-800 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold mb-4">{t('teamHistory') || 'Team History'}</h3>
                <p className="text-red-400 text-center">{t('errorLoadingHistory') || 'Error loading history'}</p>
            </div>
        );
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return t('today') || 'Today';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return t('yesterday') || 'Yesterday';
        } else {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        }
    };

    const getTotalScores = () => {
        const total = historyData.reduce(
            (acc, day) => ({
                blackWhite: acc.blackWhite + day.blackWhite,
                redWhite: acc.redWhite + day.redWhite
            }),
            { blackWhite: 0, redWhite: 0 }
        );
        return total;
    };

    const totalScores = getTotalScores();

    return (
        <div className="bg-gray-800 p-4 rounded-lg mb-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t('teamHistoryLast10Days') || 'Team History (Last 10 Days)'}</h3>
                <div className="text-sm text-gray-400">
                    {t('total') || 'Total'}: ⚫⚪ {totalScores.blackWhite} - 🔴⚪ {totalScores.redWhite}
                </div>
            </div>

            {historyData.length === 0 ? (
                <p className="text-gray-400 text-center">{t('noHistoryData') || 'No history data available'}</p>
            ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {historyData.map((day, index) => {
                        const isToday = day.isToday;
                        const difference = day.blackWhite - day.redWhite;

                        return (
                            <div
                                key={day.date}
                                className={`flex justify-between items-center p-3 rounded transition-colors ${
                                    isToday
                                        ? 'bg-blue-900 border border-blue-600'
                                        : 'bg-gray-700 hover:bg-gray-650'
                                }`}
                            >
                                <span className={`text-sm ${isToday ? 'text-blue-200 font-medium' : 'text-gray-300'}`}>
                                    {formatDate(day.date)}
                                    {isToday && <span className="ml-2 text-xs text-blue-400">({t('live') || 'Live'})</span>}
                                </span>

                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <span>⚫⚪</span>
                                        <span className="font-bold min-w-[24px] text-center">{day.blackWhite}</span>
                                    </div>

                                    <span className="text-gray-500 text-sm">vs</span>

                                    <div className="flex items-center gap-2">
                                        <span className="font-bold min-w-[24px] text-center">{day.redWhite}</span>
                                        <span>🔴⚪</span>
                                    </div>

                                    <div className="flex items-center gap-2 ml-2 min-w-[60px]">
                                        {difference > 0 ? (
                                            <span className="text-green-400 text-sm">
                                                ⚫⚪ +{difference}
                                            </span>
                                        ) : difference < 0 ? (
                                            <span className="text-green-400 text-sm">
                                                🔴⚪ +{Math.abs(difference)}
                                            </span>
                                        ) : day.blackWhite > 0 || day.redWhite > 0 ? (
                                            <span className="text-yellow-400 text-sm">{t('tie') || 'Tie'}</span>
                                        ) : (
                                            <span className="text-gray-500 text-sm">-</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}